import { motion } from "framer-motion";
import {
  Eye, Mail, TrendingUp, Search, AlertTriangle,
  BadgeCheck, Users, FileDown
} from "lucide-react";
import { useLanguage } from "@/lib/LanguageContext";

const ValueStack = () => {
  const { t } = useLanguage();

  const signalFeatures = [
    { icon: TrendingUp, title: t("value.signal.feature1.title"), desc: t("value.signal.feature1.desc") },
    { icon: Search, title: t("value.signal.feature2.title"), desc: t("value.signal.feature2.desc") },
    { icon: AlertTriangle, title: t("value.signal.feature3.title"), desc: t("value.signal.feature3.desc") },
  ];

  const outreachFeatures = [
    { icon: BadgeCheck, title: t("value.outreach.feature1.title"), desc: t("value.outreach.feature1.desc") },
    { icon: Users, title: t("value.outreach.feature2.title"), desc: t("value.outreach.feature2.desc") },
    { icon: FileDown, title: t("value.outreach.feature3.title"), desc: t("value.outreach.feature3.desc") },
  ];

  return (
    <section className="max-w-7xl mx-auto px-6 py-24">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
        className="text-center mb-16"
      >
        <h2
          className="text-3xl sm:text-4xl lg:text-5xl font-black text-white mb-4"
          style={{ fontFamily: 'Inter, sans-serif', letterSpacing: '-0.03em' }}
        >
          {t("value.title.before")}{" "}
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-amber-500">
            {t("value.title.highlight")}
          </span>
        </h2>
      </motion.div>

      <div className="grid lg:grid-cols-2 gap-8">
        {/* Left Column - The Signal */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="glass-card rounded-2xl p-8"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-xl bg-yellow-400/10 flex items-center justify-center">
              <Eye className="w-6 h-6 text-yellow-400" />
            </div>
            <div>
              <h3 className="font-bold text-xl text-white" style={{ fontFamily: 'Inter, sans-serif' }}>
                {t("value.signal.title")}
              </h3>
              <p className="text-sm text-yellow-400">{t("value.signal.subtitle")}</p>
            </div>
          </div>

          <p className="text-white/60 mb-8">
            {t("value.signal.desc")}
          </p>

          <div className="space-y-4">
            {signalFeatures.map(({ icon: Icon, title, desc }) => (
              <div key={title} className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-green-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Icon className="w-3.5 h-3.5 text-green-400" />
                </div>
                <div>
                  <h4 className="font-semibold text-white">{title}</h4>
                  <p className="text-sm text-white/50">{desc}</p>
                </div>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Right Column - The Outreach */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="glass-card rounded-2xl p-8"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-xl bg-yellow-400/10 flex items-center justify-center">
              <Mail className="w-6 h-6 text-yellow-400" />
            </div>
            <div>
              <h3 className="font-bold text-xl text-white" style={{ fontFamily: 'Inter, sans-serif' }}>
                {t("value.outreach.title")}
              </h3>
              <p className="text-sm text-yellow-400">{t("value.outreach.subtitle")}</p>
            </div>
          </div>

          <p className="text-white/60 mb-8">
            {t("value.outreach.desc")}
          </p>

          <div className="space-y-4">
            {outreachFeatures.map(({ icon: Icon, title, desc }) => (
              <div key={title} className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-green-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Icon className="w-3.5 h-3.5 text-green-400" />
                </div>
                <div>
                  <h4 className="font-semibold text-white">{title}</h4>
                  <p className="text-sm text-white/50">{desc}</p>
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ValueStack;
